import { filter, get, map, memoize, times } from 'lodash';

import {
  CUSTOM_COLLECTION_ITEM_TYPES,
  CUSTOM_COLLECTION_PRODUCT_APPEARANCES,
  CUSTOM_COLLECTION_CARD_APPEARANCES,
  CUSTOM_COLLECTION_MOBILE_LAYOUTS,
} from 'constants/collection';

const getCustomCollectionData = state => get(state, ['collection', 'customCollection']);

const getCustomCollectionProductsSortOrder = state => {
  const colorwayItems = get(getCustomCollectionData(state), 'colorwayItems');

  const products = filter(colorwayItems, { type: CUSTOM_COLLECTION_ITEM_TYPES.COLORWAY });

  const productsSortOrder = map(products, 'id');

  return productsSortOrder;
};

const getProductData = (state, handle) => get(state, ['collection', 'products', handle]);

const getMasterProductData = (state, masterHandle) =>
  get(state, ['collection', 'masterProducts', masterHandle]);

const getMasterProductCarouselIndex = (state, masterHandle) =>
  get(getMasterProductData(state, masterHandle), ['carouselIndex'], 0);

const isCustomCollectionLoading = state => get(getCustomCollectionData(state), 'isLoading', true);

const getCustomCollectionColorwayItems = state => {
  if (isCustomCollectionLoading(state)) {
    return [];
  }

  const customCollection = getCustomCollectionData(state);

  return get(customCollection, ['colorwayItems']);
};

const getCustomCollectionMasterProductItems = state => {
  if (isCustomCollectionLoading(state)) {
    return [];
  }

  const customCollection = getCustomCollectionData(state);

  return get(customCollection, ['masterProductItems']);
};

const getCustomCollectionProductAppearance = state => {
  const customCollection = getCustomCollectionData(state);

  return get(
    customCollection,
    ['productAppearance'],
    CUSTOM_COLLECTION_PRODUCT_APPEARANCES.SHOW_COLORWAYS
  );
};

const getCustomCollectionItems = state => {
  const customCollection = getCustomCollectionData(state);

  const productAppearance = get(
    customCollection,
    ['productAppearance'],
    CUSTOM_COLLECTION_PRODUCT_APPEARANCES.SHOW_COLORWAYS
  );

  switch (productAppearance) {
    case CUSTOM_COLLECTION_PRODUCT_APPEARANCES.SHOW_MASTER_PRODUCTS:
      return getCustomCollectionMasterProductItems(state);
    case CUSTOM_COLLECTION_PRODUCT_APPEARANCES.SHOW_COLORWAYS:
    default:
      return getCustomCollectionColorwayItems(state);
  }
};

const getCustomCollectionProductsData = state => {
  const customCollectionAppearance = getCustomCollectionProductAppearance(state);
  const productItems = getCustomCollectionItems(state);
  const customCollectionProductsData = map(productItems, productItem => {
    switch (customCollectionAppearance) {
      case CUSTOM_COLLECTION_PRODUCT_APPEARANCES.SHOW_MASTER_PRODUCTS:
        return getProductData(state, getMasterProductData(state, productItem.id)?.currentProduct);
      case CUSTOM_COLLECTION_PRODUCT_APPEARANCES.SHOW_COLORWAYS:
      default:
        return getProductData(state, productItem.id);
    }
  });

  return customCollectionProductsData;
};

const getCustomCollectionMobileNumberOfColumns = state => {
  const customCollection = getCustomCollectionData(state);

  const mobileLayout = get(
    customCollection,
    ['mobileLayout'],
    CUSTOM_COLLECTION_MOBILE_LAYOUTS['1_COLUMN']
  );

  switch (mobileLayout) {
    case CUSTOM_COLLECTION_MOBILE_LAYOUTS['2_COLUMN']:
      return 2;
    case CUSTOM_COLLECTION_MOBILE_LAYOUTS['1_COLUMN']:
    default:
      return 1;
  }
};

const getStoryCardData = (state, id) =>
  get(state, ['collection', 'storyCards', id]) || get(state, ['catalog', 'storyCards', id]);

const _calculateCustomCollectionLayout = ({ state, items, nColumns }) => {
  if (isCustomCollectionLoading(state)) {
    const nPlaceholderRows = 3;
    const placeholderItem = {
      type: CUSTOM_COLLECTION_ITEM_TYPES.PLACEHOLDER,
      columnSpan: 1,
      id: 'my-placeholder-id',
    };

    return times(nPlaceholderRows, () => times(nColumns, () => placeholderItem));
  }

  const rows = [];
  let currentRow;
  let currentColumn;

  const addNewRow = () => {
    currentRow = [];
    currentColumn = 0;
    rows.push(currentRow);
  };

  const getPreferredColumnSpan = item => {
    switch (item.type) {
      case CUSTOM_COLLECTION_ITEM_TYPES.STORY_CARD: {
        const storyCard = getStoryCardData(state, item.id);

        if (
          get(storyCard, 'preferredAppearance') === CUSTOM_COLLECTION_CARD_APPEARANCES['2_COLUMN']
        ) {
          return 2;
        }

        return 1;
      }
      case CUSTOM_COLLECTION_ITEM_TYPES.SUB_HEADER:
        return nColumns;
      case CUSTOM_COLLECTION_ITEM_TYPES.COLORWAY:
      case CUSTOM_COLLECTION_ITEM_TYPES.MASTER_PRODUCT:
      default:
        return 1;
    }
  };

  // Add first row
  addNewRow();

  for (let i = 0; i < items.length; i += 1) {
    const item = items[i];
    item.preferredColumnSpan = getPreferredColumnSpan(item);
    const itemIsWholeRow = item.type === CUSTOM_COLLECTION_ITEM_TYPES.SUB_HEADER;

    if (itemIsWholeRow) {
      if (currentColumn > 0) {
        addNewRow();
      }

      currentRow.push({ ...item, columnSpan: item.preferredColumnSpan });
      addNewRow();
    } else {
      let remainingSpace = nColumns - currentColumn;

      // If no more remaining space, roll over to new row
      if (remainingSpace < 1) {
        addNewRow();
        remainingSpace = nColumns - currentColumn;
      }

      const columnSpan = remainingSpace <= 1 ? 1 : item.preferredColumnSpan;

      // Add element, and make it span its preferred span if there is room for it, otherwise coerce it into 1 column if end of row
      currentRow.push({ ...item, columnSpan });
      currentColumn += columnSpan;
    }
  }

  return rows;
};

const calculateCustomCollectionLayout = memoize(
  _calculateCustomCollectionLayout,
  ({ state, items, nColumns }) =>
    `${isCustomCollectionLoading(state)}--${nColumns}--${items.length}`
);

export {
  getProductData,
  getMasterProductData,
  getMasterProductCarouselIndex,
  getCustomCollectionData,
  getCustomCollectionProductsSortOrder,
  calculateCustomCollectionLayout,
  getCustomCollectionMasterProductItems,
  getCustomCollectionColorwayItems,
  getCustomCollectionItems,
  getCustomCollectionMobileNumberOfColumns,
  getCustomCollectionProductAppearance,
  getCustomCollectionProductsData,
  getStoryCardData,
};
